import React, { useState, useEffect } from 'react';
import { BarChart3, Users, Eye, TrendingUp, Calendar, Globe, RefreshCw, Trash2 } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '../App';
import { toast, Toaster } from 'sonner';
import { useTranslation } from 'react-i18next';

const VisitorAnalytics = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uniqueVisitors, setUniqueVisitors] = useState([]);

  useEffect(() => {
    if (user.role !== 'super_admin') {
      toast.error('Admin access required');
      window.location.href = '/dashboard';
    } else {
      fetchStats();
      fetchUniqueVisitors(); // Load unique visitors by default
    }
  }, [user]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchUniqueVisitors = async () => {
    try {
      const response = await axios.get(`${API}/analytics/unique-visitors`);
      setUniqueVisitors(response.data.unique_visitors || []);
    } catch (error) {
      console.error('Error fetching unique visitors:', error);
      toast.error('Failed to load unique visitors');
    }
  };

  const clearAnalytics = async () => {
    if (!window.confirm('Are you sure you want to clear all analytics data? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/analytics/clear`);
      toast.success('Analytics data cleared');
      fetchStats();
    } catch (error) {
      console.error('Error clearing analytics:', error);
      toast.error('Failed to clear analytics');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-6 sm:py-8 pt-24">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navigation user={user} setUser={setUser} />
      <Toaster position="top-right" richColors />

      <div className="container mx-auto px-4 py-6 sm:py-8 pt-24">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                <BarChart3 className="w-10 h-10 text-blue-600" />
                Visitor Analytics
              </h1>
              <p className="text-gray-600 mt-2">Website traffic and visitor statistics</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={fetchStats} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={clearAnalytics} variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Data
              </Button>
            </div>
          </div>

          {/* Stats Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <Card className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <div className="flex items-center justify-between mb-2">
                <Eye className="w-8 h-8 opacity-80" />
                <span className="text-sm opacity-80">All Time</span>
              </div>
              <h3 className="text-3xl font-bold mb-1">{stats?.total_visits?.toLocaleString() || 0}</h3>
              <p className="text-sm opacity-90">Total Page Views</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-8 h-8 opacity-80" />
                <span className="text-sm opacity-80">By IP</span>
              </div>
              <h3 className="text-3xl font-bold mb-1">{stats?.unique_visitors?.toLocaleString() || 0}</h3>
              <p className="text-sm opacity-90">Unique Visitors</p>
              <p className="text-xs opacity-75 mt-2">Distinct IP addresses</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 opacity-80" />
                <span className="text-sm opacity-80">Sessions</span>
              </div>
              <h3 className="text-3xl font-bold mb-1">{stats?.total_sessions?.toLocaleString() || 0}</h3>
              <p className="text-sm opacity-90">Total Sessions</p>
              <p className="text-xs opacity-75 mt-2">30min window</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <div className="flex items-center justify-between mb-2">
                <Calendar className="w-8 h-8 opacity-80" />
                <span className="text-sm opacity-80">{t('time.today')}</span>
              </div>
              <h3 className="text-3xl font-bold mb-1">{stats?.today_visits?.toLocaleString() || 0}</h3>
              <p className="text-sm opacity-90">Today's Visits</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 opacity-80" />
                <span className="text-sm opacity-80">7 Days</span>
              </div>
              <h3 className="text-3xl font-bold mb-1">{stats?.this_week_visits?.toLocaleString() || 0}</h3>
              <p className="text-sm opacity-90">This Week</p>
            </Card>
          </div>

          {/* Charts & Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Top Pages */}
            <Card className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                Top Pages
              </h3>
              {stats?.top_pages && stats.top_pages.length > 0 ? (
                <div className="space-y-3">
                  {stats.top_pages.map((page, idx) => (
                    <div key={`item-${idx}`} className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">{page.page}</p>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${(page.visits / stats.top_pages[0].visits) * 100}%`
                            }}
                          ></div>
                        </div>
                      </div>
                      <span className="ml-4 text-sm font-semibold text-gray-600">{page.visits}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No data available</p>
              )}
            </Card>

            {/* Visits by Date (Last 30 days) */}
            <Card className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-600" />
                Visits by Date (Last 30 Days)
              </h3>
              {stats?.visits_by_date && stats.visits_by_date.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {stats.visits_by_date.reverse().map((day, idx) => (
                    <div key={`item-${idx}`} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{new Date(day.date).toLocaleDateString()}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full"
                            style={{
                              width: `${(day.visits / Math.max(...stats.visits_by_date.map(d => d.visits))) * 100}%`
                            }}
                          ></div>
                        </div>
                        <span className="font-semibold text-gray-800 w-12 text-right">{day.visits}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No data available</p>
              )}
            </Card>
          </div>

          {/* Geographic Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Visits by Country */}
            <Card className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-purple-600" />
                Visits by Country
              </h3>
              {stats?.visits_by_country && stats.visits_by_country.length > 0 ? (
                <div className="space-y-3">
                  {stats.visits_by_country.map((location, idx) => (
                    <div key={`item-${idx}`} className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{location.country}</p>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className="bg-purple-600 h-2 rounded-full"
                            style={{
                              width: `${(location.visits / stats.visits_by_country[0].visits) * 100}%`
                            }}
                          ></div>
                        </div>
                      </div>
                      <span className="ml-4 text-sm font-semibold text-gray-600">{location.visits}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No location data available</p>
              )}
            </Card>

            {/* Visits by City */}
            <Card className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                Top Cities
              </h3>
              {stats?.visits_by_city && stats.visits_by_city.length > 0 ? (
                <div className="space-y-3">
                  {stats.visits_by_city.map((location, idx) => (
                    <div key={`item-${idx}`} className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{location.city}</p>
                        <p className="text-xs text-gray-500">{location.country}</p>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className="bg-indigo-600 h-2 rounded-full"
                            style={{
                              width: `${(location.visits / stats.visits_by_city[0].visits) * 100}%`
                            }}
                          ></div>
                        </div>
                      </div>
                      <span className="ml-4 text-sm font-semibold text-gray-600">{location.visits}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No city data available</p>
              )}
            </Card>
          </div>

          {/* Recent Visits - Unique IPs Only */}
          <Card className="p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-600" />
              Recent Visits (Unique IPs - Latest Visit Only)
            </h3>
            {stats?.recent_visits && stats.recent_visits.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Time</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Location</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">IP Address</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Session</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Page</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Referrer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_visits.map((visit, idx) => (
                      <tr key={`item-${idx}`} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                          {new Date(visit.timestamp).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {visit.location ? (
                            <div>
                              <p className="font-medium">{visit.location.city}</p>
                              <p className="text-xs text-gray-500">{visit.location.country}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400">Unknown</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-blue-600 font-mono">
                          {visit.ip_address || 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {visit.session_type === 'new_session' ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-semibold">
                              New
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              Return
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-800 font-medium">{visit.page}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{visit.referrer}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No recent visits</p>
            )}
          </Card>

          {/* All Unique IP Addresses with Visit Counts */}
          {stats?.unique_ips && stats.unique_ips.length > 0 && (
            <Card className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                All Unique IP Addresses ({stats.unique_ips.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">#</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">IP Address</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Location</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Total Visits</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">First Visit</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Last Visit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.unique_ips.map((ip, idx) => (
                      <tr key={`item-${idx}`} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {idx + 1}
                        </td>
                        <td className="py-3 px-4 text-sm text-blue-600 font-mono font-semibold">
                          {ip.ip_address}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {ip.location && ip.location.city && ip.location.city !== t('common.unknown') ? (
                            <div>
                              <p className="font-medium">{ip.location.city}</p>
                              <p className="text-xs text-gray-500">{ip.location.country}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400">Unknown</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full font-semibold">
                            {ip.visit_count} {ip.visit_count === 1 ? 'visit' : 'visits'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                          {new Date(ip.first_visit).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                          {new Date(ip.last_visit).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Unique Visitors Details - Always shown */}
          {uniqueVisitors.length > 0 && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  Unique Visitors by IP Address ({uniqueVisitors.length})
                </h3>
                <Button
                  onClick={() => { fetchStats(); fetchUniqueVisitors(); }}
                  variant="outline"
                  size="sm"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">IP Address</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Total Visits</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">First Visit</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Last Visit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueVisitors.map((visitor, idx) => (
                      <tr key={`item-${idx}`} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-blue-600 font-mono font-semibold">
                          {visitor.ip_address}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-800">
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full font-semibold">
                            {visitor.visit_count}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(visitor.first_visit).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(visitor.last_visit).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Showing top 100 unique visitors based on IP addresses
              </p>
            </Card>
          )}

          {/* Monthly Summary */}
          <Card className="p-6 mt-6 bg-gradient-to-r from-blue-50 to-purple-50">
            <h3 className="text-xl font-bold text-gray-800 mb-4">30-Day Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Visits</p>
                <p className="text-3xl font-bold text-blue-600">{stats?.this_month_visits?.toLocaleString() || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Avg Visits/Day</p>
                <p className="text-3xl font-bold text-green-600">
                  {stats?.this_month_visits ? Math.round(stats.this_month_visits / 30) : 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Top Page</p>
                <p className="text-lg font-semibold text-purple-600 truncate">
                  {stats?.top_pages?.[0]?.page || 'N/A'}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default VisitorAnalytics;
